import { useRef, useState } from 'react';
import { Camera, ImagePlus, X, Loader2, ZoomIn, ChevronLeft, ChevronRight } from 'lucide-react';
import { http, API } from '../lib/api';

export const fileUrl = (id) => `${API}/files/${id}`;

async function shrink(file) {
  if (!file.type.startsWith('image/') || file.type === 'image/heic' || file.type === 'image/heif') return file;
  const bitmap = await createImageBitmap(file).catch(() => null);
  if (!bitmap) return file;
  const max = 1600; const scale = Math.min(1, max / Math.max(bitmap.width, bitmap.height));
  if (scale === 1 && file.size < 1.5 * 1024 * 1024) return file;
  const canvas = document.createElement('canvas');
  canvas.width = Math.round(bitmap.width * scale); canvas.height = Math.round(bitmap.height * scale);
  canvas.getContext('2d').drawImage(bitmap, 0, 0, canvas.width, canvas.height);
  const blob = await new Promise(res => canvas.toBlob(res, 'image/jpeg', 0.85));
  return new File([blob], file.name.replace(/\.\w+$/, '') + '.jpg', { type: 'image/jpeg' });
}

export function Lightbox({ photos, index, onClose, onMove }) {
  return <div className="lightbox" onClick={onClose} data-testid="photo-lightbox">
    <button type="button" className="lightbox-close" onClick={onClose} data-testid="lightbox-close"><X size={20} /></button>
    {photos.length > 1 && <button type="button" className="lightbox-nav prev" onClick={(e) => { e.stopPropagation(); onMove(-1); }} data-testid="lightbox-prev"><ChevronLeft size={22} /></button>}
    <img src={fileUrl(photos[index])} alt={`Fotoğraf ${index + 1}`} onClick={(e) => e.stopPropagation()} />
    {photos.length > 1 && <button type="button" className="lightbox-nav next" onClick={(e) => { e.stopPropagation(); onMove(1); }} data-testid="lightbox-next"><ChevronRight size={22} /></button>}
    <span className="lightbox-count">{index + 1} / {photos.length}</span>
  </div>;
}

export function PhotoThumbs({ photos = [], size = 'sm', testId = 'photo-thumbs' }) {
  const [open, setOpen] = useState(null);
  if (!photos.length) return null;
  return <>
    <div className={`photo-thumbs ${size}`} data-testid={testId}>
      {photos.slice(0, 3).map((id, i) => <button type="button" key={id} className="thumb" onClick={(e) => { e.stopPropagation(); setOpen(i); }} data-testid={`${testId}-${i}`}><img src={fileUrl(id)} alt="" loading="lazy" />{i === 2 && photos.length > 3 && <b>+{photos.length - 3}</b>}</button>)}
    </div>
    {open !== null && <Lightbox photos={photos} index={open} onClose={() => setOpen(null)} onMove={(d) => setOpen((open + d + photos.length) % photos.length)} />}
  </>;
}

export function PhotoUploader({ value = [], onChange, label = 'Fotoğraflar' }) {
  const [busy, setBusy] = useState(0);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(null);
  const cameraRef = useRef(); const fileRef = useRef();
  const upload = async (files) => {
    setError(''); setBusy(files.length);
    const ids = [...value];
    for (const raw of files) {
      try {
        const file = await shrink(raw);
        const form = new FormData(); form.append('file', file);
        const r = await http.post('/upload', form, { headers: { 'Content-Type': 'multipart/form-data' } });
        ids.push(r.data.id); onChange([...ids]);
      } catch (e) { setError(e.response?.data?.detail || 'Yükleme başarısız oldu'); }
      setBusy(n => n - 1);
    }
  };
  const remove = async (id) => { onChange(value.filter(x => x !== id)); http.delete(`/files/${id}`).catch(() => {}); };
  return <div className="photo-field full-field" data-testid="photo-uploader">
    <span className="photo-label">{label} <small>{value.length ? `${value.length} fotoğraf` : 'Telefonunuzdan çekin veya galeriden seçin'}</small></span>
    <div className="photo-grid">
      {value.map((id, i) => <div className="photo-item" key={id} data-testid={`photo-item-${i}`}>
        <img src={fileUrl(id)} alt="" onClick={() => setOpen(i)} />
        <button type="button" className="photo-zoom" onClick={() => setOpen(i)} aria-label="Büyüt"><ZoomIn size={13} /></button>
        <button type="button" className="photo-remove" onClick={() => remove(id)} aria-label="Kaldır" data-testid={`photo-remove-${i}`}><X size={13} /></button>
      </div>)}
      {busy > 0 && <div className="photo-item uploading" data-testid="photo-uploading"><Loader2 size={20} className="spin" /><span>Yükleniyor…</span></div>}
      <button type="button" className="photo-add" onClick={() => cameraRef.current.click()} data-testid="photo-camera-button"><Camera size={20} /><span>Fotoğraf çek</span></button>
      <button type="button" className="photo-add" onClick={() => fileRef.current.click()} data-testid="photo-gallery-button"><ImagePlus size={20} /><span>Galeriden seç</span></button>
    </div>
    <input ref={cameraRef} type="file" accept="image/*" capture="environment" hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} data-testid="photo-camera-input" />
    <input ref={fileRef} type="file" accept="image/*,application/pdf" multiple hidden onChange={(e) => { upload([...e.target.files]); e.target.value = ''; }} data-testid="photo-file-input" />
    {error && <div className="photo-error" data-testid="photo-error">{error}</div>}
    {open !== null && <Lightbox photos={value} index={open} onClose={() => setOpen(null)} onMove={(d) => setOpen((open + d + value.length) % value.length)} />}
  </div>;
}
